import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config';

export function useBuscarAbonado() {
    const searchResult = ref(null);
    const editedEmail = ref('');
    const emailToBind = ref('');
    const step = ref('search');
    const loading = ref(false);
    const error = ref(null);
    const errorMessage = ref(null);

    // Helper to get the data object (handles both nested {data: {...}} and flat structures)
    const getData = () => {
        const result = searchResult.value;
        if (!result) return {};
        return result.data ?? result;
    };

    const emailChanged = computed(() => {
        if (!searchResult.value) return false;
        const data = getData();
        const originalEmail = data.user_email ?? data.email ?? '';
        return editedEmail.value !== originalEmail;
    });

    // Map API error messages to error codes (DRY — single source of truth)
    const resolveErrorCode = message => {
        const text = String(message ?? '');
        if (text.includes('ya está vinculado') || text.includes('already linked')) {
            return 'ALREADY_LINKED';
        }
        return 'BIND_FAILED';
    };

    const entitlementId = computed(() => {
        const result = searchResult.value;
        if (!result) return null;
        // entitlement_id may be at root level or inside the data object
        return result.entitlement_id ?? getData().entitlement_id ?? null;
    });
    // Type of entitlement found by the backend: 'abono' | 'membresia' (default 'abono')
    const entitlementType = computed(() => getData().type ?? 'abono');

    const searchAbonado = async({ subscriber_number, beneficiary_id_doc, pin }) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await store.dispatch(config.getVuexAlias('sga.buscar.abono'), {
                filters: { subscriber_number, beneficiary_id_doc, pin }
            });

            if (response?.data) {
                searchResult.value = response.data;
                // Data may be nested (response.data.data) or flat — use getData() for consistency
                const sgaData = (response.data.data ?? response.data);
                const email = sgaData.user_email ?? sgaData.email ?? '';
                editedEmail.value = email;
                emailToBind.value = email;  // Set BEFORE step change (BLOCKING 1)
                step.value = 'confirm';
                return true;
            }
            return false;
        } catch (err) {
            error.value = 'NOT_FOUND';
            console.error('[useBuscarAbonado] search failed:', err);
            return false;
        } finally {
            loading.value = false;
        }
    };

    const bindAbonado = async({ email, password } = {}) => {
        if (!entitlementId.value) {
            error.value = 'BIND_FAILED';
            return null;
        }

        loading.value = true;
        error.value = null;

        try {
            const payload = { entitlement_id: entitlementId.value, password };
            if (email) {
                payload.email = email;
            }

            const response = await store.dispatch(config.getVuexAlias('sga.abono.bind'), payload);

            if (!response?.data?.success) {
                const message = response?.data?.message ?? 'Error al vincular abonado';
                error.value = resolveErrorCode(message);
                errorMessage.value = message;
                return null;
            }

            return response.data;
        } catch (err) {
            const apiMessage = err.response?.data?.message ?? err.message;
            if (!error.value) {
                error.value = resolveErrorCode(apiMessage);
            }
            if (!errorMessage.value) {
                errorMessage.value = apiMessage;
            }
            console.error('[useBuscarAbonado] bind failed:', err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const goToSuccess = () => {
        step.value = 'success';
    };

    const reset = () => {
        step.value = 'search';
        searchResult.value = null;
        editedEmail.value = '';
        emailToBind.value = '';
        error.value = null;
        errorMessage.value = null;
    };

    // Display computed properties
    const displayEmail = computed(() => {
        const result = searchResult.value;
        if (!result) return '';
        const data = result.data ?? result;
        return data.user_email ?? data.email ?? '';
    });

    const displayName = computed(() => {
        const result = searchResult.value;
        if (!result) return '';
        const data = result.data ?? result;
        return `${data.name ?? ''} ${data.lastName ?? ''}`.trim();
    });

    const displayIdentification = computed(() => {
        const result = searchResult.value;
        if (!result) return '';
        const data = result.data ?? result;
        return data.identification?.value ?? '';
    });

    const displayAddress = computed(() => {
        const result = searchResult.value;
        if (!result) return '';
        const data = result.data ?? result;
        if (!data.address) return '';
        const { streetLine1, city, zipCode, country } = data.address;
        const parts = [streetLine1, city, zipCode, country].filter(Boolean);
        return parts.join(', ');
    });

    return {
        searchResult,
        editedEmail,
        emailToBind,
        step,
        loading,
        error,
        errorMessage,
        emailChanged,
        entitlementId,
        entitlementType,
        searchAbonado,
        bindAbonado,
        goToSuccess,
        reset,
        displayEmail,
        displayName,
        displayIdentification,
        displayAddress
    };
}
